import classNames from 'classnames';
import { debounce, get } from 'lodash';
import { FC, useCallback, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import Icon from '~/components/Icon';
import ChangeVariantModal from '~/components/Modals/ChangeVariantModal';
import Quantity from '~/components/Quantity';
import Text from '~/components/Text';
import Colors from '~/constants/colors';
import { PATH } from '~/constants/enum';
import { useCart } from '~/data/cart';
import { CartAtom, CartItem } from '~/services/cart';
import { ProductVariant } from '~/services/product';
import { trackingQuantityChange, trackingRemoveItemFromCart } from '~/services/tracking';
import { formatNumber } from '~/utils/number';
import { getUrlCustom } from '~/utils/theme';
import styles from './QuickCartItem.module.scss';
import EditIcon from '~/assets/svgs/edit-2.svg';
import TrashIcon from '~/assets/svgs/trash.svg';

interface QuickCartItemProps {
    data: CartItem;
}

const QuickCartItem: FC<QuickCartItemProps> = ({ data }) => {
    const cart = useRecoilValue(CartAtom.cartSelector);
    const setCart = useSetRecoilState(CartAtom.cart);
    const [openChangeVariantModal, setOpenChangeVariantModal] = useState(false);

    const {
        updateCartItems,
        removeCartItem,
        loadingRemoveCartItem,
        loadingUpdateCartItems,
        loadingAddToCart,
        addToCart,
        getCartInfo
    } = useCart();

    const debounceUpdateCartItem = useCallback(
        debounce(
            (cartItemUId: string, quantity: number) =>
                updateCartItems({
                    variables: {
                        cartId: cart.cartId,
                        cartItemUId,
                        quantity
                    }
                }),
            500
        ),
        []
    );

    const handleChangeQuantity = (cartItemUId: string, quantity: number) => {
        setCart((prevCart) => ({
            ...prevCart,
            items: prevCart.items?.map((cartItem) => {
                if (cartItem.uid === cartItemUId) {
                    trackingQuantityChange({
                        quantity,
                        variant: cartItem.selectedVariant
                    });
                    return { ...cartItem, quantity };
                }

                return cartItem;
            })
        }));
        debounceUpdateCartItem(cartItemUId, quantity);
    };

    const handleDelete = async (cartItem: CartItem) => {
        const cartItemId = get(cartItem, 'id', '');

        trackingRemoveItemFromCart(cartItem);

        return await removeCartItem({
            variables: {
                cartId: cart.cartId,
                cartItemId
            }
        });
    };

    const onChangeProductVariant = useCallback(
        async (
            {
                selectedVariant,
                productNote
            }: {
                selectedVariant: Partial<ProductVariant>;
                productNote: { name: string; type: string; value: string }[];
            },
            parentSKU: string
        ) => {
            if (selectedVariant?.product?.sku === data.selectedVariant?.product?.sku) {
                await updateCartItems({
                    variables: {
                        cartId: cart.cartId,
                        cartItemUId: data.uid,
                        quantity: data.quantity,
                        productNote,
                        openCart: true
                    }
                });
            } else {
                await addToCart({
                    variables: {
                        sku: selectedVariant?.product?.sku,
                        quantity: data.quantity,
                        cartId: cart.cartId as any,
                        parentSku: data?.product?.sku || '',
                        productNote,
                        openCart: true
                    }
                });
                await handleDelete(data);
            }

            setOpenChangeVariantModal(false);
        },
        [cart]
    );

    return (
        <div className={classNames(styles.cart_item, 'relative')}>
            <div className="relative flex-shrink-0 w-1/5 aspect-square">
                <img
                    className="object-cover"
                    src={data.product.image?.url || '/images/example_product.png'}
                    alt={data.product.name || ''}
                />
            </div>
            <div className="flex flex-col gap-1 flex-1">
                <a
                    href={getUrlCustom({
                        pathname: PATH.ProductDetail,
                        query: {
                            variant: data.selectedVariant?.product?.id,
                            slug: data.product?.slug
                        }
                    })}
                >
                    <Text type="title-5" className="line-clamp-2">
                        {data.selectedVariant?.product?.name || data.product.name}
                    </Text>
                </a>

                <div className="flex items-center gap-2">
                    <Text type="body-3" className="text-grey-700 capitalize">
                        {data.selectedVariant?.attributes?.map((attr) => attr.label).join(' / ')}
                    </Text>
                    <EditIcon
                        className="cursor-pointer size-4"
                        // name="edit-2"
                        color={Colors.GREY_400}
                        onClick={() => setOpenChangeVariantModal(true)}
                    />
                </div>
                <div className="flex items-end justify-between">
                    <div className="flex flex-wrap items-end gap-1">
                        <Text type="title-3" className="text-primary">
                            {get(data, 'selectedVariant.product.price_range.minimum_price.final_price.symbol', '$')}
                            {formatNumber(
                                data.selectedVariant?.product?.price_range?.minimum_price?.final_price?.value,
                                2
                            )}
                        </Text>
                        {!!data.selectedVariant?.product?.price_range.minimum_price?.discount?.percent_off && (
                            <Text type="body-3" className="line-through text-grey-400">
                                {get(
                                    data,
                                    'selectedVariant.product.price_range.minimum_price.regular_price.symbol',
                                    '$'
                                )}
                                {formatNumber(
                                    data.selectedVariant?.product.price_range.minimum_price?.regular_price?.value,
                                    2
                                )}
                            </Text>
                        )}
                        {!!data?.productNote?.length && (
                            <div className="flex flex-wrap">
                                {data?.productNote.map((note: any, index: number) => (
                                    <div key={index} className="flex flex-nowrap items-center">
                                        <Text type="body-3" color={Colors.GREY_700}>
                                            <b>{note.name}</b>: {note.value}
                                        </Text>
                                        <span className="mx-1">
                                            {' '}
                                            {index === data?.productNote.length - 1 ? null : '|'}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <Quantity
                        value={data.quantity}
                        maxValue={data.product.quantity}
                        onChange={(value) => handleChangeQuantity(data.uid || '', value)}
                    />
                </div>
            </div>
            <span className="flex">
                {loadingRemoveCartItem ? (
                    <Icon
                        className="cursor-pointer animate-spin"
                        name="status"
                        color={Colors.GREY_100}
                        onClick={() => handleDelete(data)}
                    />
                ) : (
                    <TrashIcon
                        className="cursor-pointer size-4"
                        color={Colors.GREY_100}
                        onClick={() => handleDelete(data)}
                    />
                )}
            </span>
            {openChangeVariantModal && (
                <ChangeVariantModal
                    open={openChangeVariantModal}
                    product={data.product}
                    defaultProductNote={data.productNote}
                    defaultVariant={data.selectedVariant}
                    onConfirm={onChangeProductVariant}
                    loading={loadingAddToCart || loadingUpdateCartItems || loadingRemoveCartItem}
                    onCancel={() => setOpenChangeVariantModal(false)}
                />
            )}
        </div>
    );
};

export default QuickCartItem;
